import React, { useEffect } from 'react';
import { Modal } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { Banner, BannerButton } from '../../../models';
import { isExternalLink } from '../../../utils';
import ModalImageContent from './components/ModalImageContent';
import ModalFullContent from './components/ModalFullContent';
import styles from './ModalBanner.module.scss';

export type BannerClickTrackingOptions = {
    buttonId?: string;
    url?: string;
};

type Props = {
    banner: Banner;
    visible: boolean;
    onClose: () => void;
    onView?: (bannerId: string) => void | Promise<void>;
    onButtonClick?: (bannerId: string, opts?: BannerClickTrackingOptions) => void | Promise<void>;
    previewMode?: boolean;
}

const ModalBanner: React.FC<Props> = observer(({
    banner,
    visible,
    onClose,
    onView,
    onButtonClick,
    previewMode
}) => {
    useEffect(() => {
        if (visible && !previewMode && onView) {
            onView(banner.id);
        }
    }, [banner.id, visible, onView, previewMode]);

    const handleButtonClick = async (button: BannerButton) => {
        if (previewMode) return;

        if (onButtonClick) {
            await onButtonClick(banner.id, { buttonId: button.id, url: button.url });
        }
        if (button.openInNewWindow) {
            window.open(button.url, '_blank', 'noopener,noreferrer');
        } else {
            window.location.href = button.url;
        }
    };

    const handleImageClick = async () => {
        if (previewMode) return;

        if (banner.imageUrl && banner.bannerLink) {
            if (onButtonClick) {
                await onButtonClick(banner.id, { url: banner.bannerLink });
            }
            const url = banner.bannerLink;
            if (isExternalLink(url)) {
                window.open(url, '_blank', 'noopener,noreferrer');
            } else {
                window.location.href = url;
            }
        }
    };

    const handleContentLinkClick = async (url: string) => {
        if (previewMode || !onButtonClick) return;
        await onButtonClick(banner.id, { url });
    };

    const hasText = !!(banner.text && banner.text.trim());
    const hasButtons = !!(banner.buttons && banner.buttons.length > 0);
    const hasContent = hasText || hasButtons;
    const isImageOnly = !!(banner.imageUrl && !hasContent);

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            maskClosable={false}
            footer={null}
            closable={true}
            closeIcon={<CloseOutlined />}
            width={600}
            className={styles.modalBanner}            
            bodyStyle={{ padding: '0px' }}
        >
            {isImageOnly ? (
                <ModalImageContent
                    banner={banner}
                    onClick={handleImageClick}
                    previewMode={previewMode}
                />
            ) : (
                <ModalFullContent
                    banner={banner}
                    onImageClick={handleImageClick}
                    onButtonClick={handleButtonClick}
                    onContentLinkClick={!previewMode && onButtonClick ? handleContentLinkClick : undefined}
                    previewMode={previewMode}
                />
            )}
        </Modal>
    );
});

export default ModalBanner;
